import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Oval as Loader } from "react-loader-spinner";
import CreatorCard from "../../../../../features/cards/CreatorCard";
import ExpiryList from "./ExpiryList";
import useSwipe from "../../../../../shared/hooks/useSwipe";
import "../home.css";
import {
  getSubscriptionCounts,
  getUserSubscriptions
} from "../../../../../shared/actions/userActions";
import { ROLE } from "../../../../../shared/constants/common";
import { resolveVatRate } from "../../../../../shared/utils/paymentUtil";
import { reportError } from "../../../../../shared/sentry";

// Constants
const LIMIT = 9;

const API_FILTER_MAP = {
  all: undefined,
  subscriptions: 'subscription',
  following: 'follow',
  expire: 'expire',
  expiresoon: 'expiresoon',
};

const FILTER_BUTTONS = [
  { value: 'all', label: 'All' },
  { value: 'subscriptions', label: 'Subscriptions' },
  { value: 'following', label: 'Following' },
  { value: 'expiresoon', label: 'Expiring Soon' },
  { value: 'expire', label: 'Expired' },
];

const EMPTY_MESSAGES = {
  all: 'No creators found',
  subscriptions: 'No active subscriptions',
  following: 'No creators followed yet',
  expiresoon: 'No subscriptions expiring soon',
  expire: 'No expired subscriptions',
};


const SubscriptionsTab = ({
  userInfo,
}) => {
  const [vatPercentage, setVatPercentage] = useState(0);
  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(userInfo);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [userInfo]);

  const [subscriptionCounts, setSubscriptionCounts] = useState({
    total: 0,
    active: 0,
    expired: 0,
    expiringSoon: 0
  });
  const [expiringSoonList, setExpiringSoonList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    initial: true,
    subscriptions: false,
    counts: false,
    pagination: false
  });
  const [errors, setErrors] = useState({
    subscriptions: null,
    counts: null
  });
  const [filter, setFilter] = useState("all");
  const [filterLabel, setFilterLabel] = useState("All");
  const hasSubscriptionsRef = useRef(false);
  const fetchRequestRef = useRef(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState(null); // 'next' | 'prev' during 350ms
  const gridContainerRef = useRef(null);

  const fetchSubscriptionCounts = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, counts: true }));
    setErrors(prev => ({ ...prev, counts: null }));

    try {
      const response = await getSubscriptionCounts();
      if (response.success && response.data) {
        if (response.data.counts) {
          setSubscriptionCounts(response.data.counts);
        }
        if (Array.isArray(response.data.expiringSoonList)) {
          setExpiringSoonList(response.data.expiringSoonList);
        }
        setErrors(prev => ({ ...prev, counts: null }));
      } else {
        throw new Error(response.error || 'Failed to fetch subscription counts');
      }
    } catch (error) {
      reportError(error, 'subscriptionsTab subscription counts');
      setErrors(prev => ({
        ...prev,
        counts: error.message || 'Failed to load subscription counts'
      }));
    } finally {
      setLoadingStates(prev => ({ ...prev, counts: false }));
    }
  }, []);

  const transformSubscription = useCallback((subscription) => ({
    subscriptionId: subscription._id,
    _id: subscription.ccreator._id,
    username: subscription.ccreator?.username || '',
    first_name: subscription.ccreator?.first_name || '',
    last_name: subscription.ccreator?.last_name || '',
    profile_picture: subscription.ccreator?.profile_picture || '',
    isSubscribed: subscription.type === 'subscription',
    isFollowing: subscription.type === 'follow',
    rol: subscription.ccreator?.rol || ROLE.CREATOR_CONTENT,
    type: subscription.type,
    createdAt: subscription.created_at,
    updatedAt: subscription.update_at,
    state: subscription.state,
    expireAt: subscription.expireAt,
    expire_at: subscription.expire_at,
    price: subscription.ccreator?.price || '',
    paidAmount: subscription.ccreator?.paidAmount || '',
    applied_promotion: subscription.ccreator?.applied_promotion && Object.keys(subscription.ccreator?.applied_promotion).length > 0 ?
      subscription.ccreator?.applied_promotion :
      null,
    paymentForm: subscription.ccreator?.type_subscription?.payment?.paymentForm || null,
  }), []);

  const loadSubscriptions = useCallback(async (page = 1) => {
    const requestId = ++fetchRequestRef.current;
    const isPagination = hasSubscriptionsRef.current;

    setLoadingStates(prev => ({
      ...prev,
      subscriptions: true,
      pagination: isPagination,
      initial: !isPagination && prev.initial
    }));
    setErrors(prev => ({ ...prev, subscriptions: null }));

    try {
      const apiFilter = API_FILTER_MAP[filter];
      const params = {
        limit: LIMIT,
        page: page - 1
      };
      if (apiFilter) {
        params.filter = apiFilter;
      }

      const response = await getUserSubscriptions(params);

      if (requestId !== fetchRequestRef.current) {
        return;
      }

      if (response.success && response.data) {
        const { data, totalPages: apiTotalPages, recordsTotal } = response.data;
        const subscriptionList = Array.isArray(data) ? data : [];
        const transformedSubscriptions = subscriptionList.map(transformSubscription);

        setSubscriptions(transformedSubscriptions);
        setCurrentPage(page);
        hasSubscriptionsRef.current = transformedSubscriptions.length > 0;
        setTotalPages(apiTotalPages || Math.ceil(recordsTotal / LIMIT));
        setErrors(prev => ({ ...prev, subscriptions: null }));
      } else {
        throw new Error(response.error || 'Failed to fetch subscriptions');
      }
    } catch (error) {
      if (requestId !== fetchRequestRef.current) {
        return;
      }
      reportError(error, 'subscriptionsTab fetch subscriptions');
      setErrors(prev => ({
        ...prev,
        subscriptions: error.message || 'Failed to load subscriptions'
      }));
    } finally {
      if (requestId !== fetchRequestRef.current) {
        return;
      }
      setLoadingStates(prev => ({
        ...prev,
        subscriptions: false,
        pagination: false,
        initial: false
      }));
    }
  }, [filter, transformSubscription]);

  const refreshAfterSubscriptionSuccess = useCallback(async () => {
    await fetchSubscriptionCounts();
    await loadSubscriptions(currentPage);
  }, [fetchSubscriptionCounts, loadSubscriptions, currentPage]);

  const minSwipeDistance = 50;
  const handleSubscriptionsSwipe = useCallback((startX, endX, startY, endY) => {
    if (startX == null || endX == null) return;
    const distanceX = startX - endX;
    const distanceY = startY != null && endY != null ? Math.abs(startY - endY) : 0;
    if (startY != null && endY != null && distanceY > Math.abs(distanceX)) {
      return;
    }
    if (distanceX > minSwipeDistance && currentPage < totalPages) {
      setTransitionDirection('next');
      setIsTransitioning(true);
      loadSubscriptions(currentPage + 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 350);
    } else if (distanceX < -minSwipeDistance && currentPage > 1) {
      setTransitionDirection('prev');
      setIsTransitioning(true);
      loadSubscriptions(currentPage - 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 350);
    }
  }, [currentPage, totalPages, loadSubscriptions]);

  const { ref: swipeRef, handlers: swipeHandlers, swipeOffset, swipeDirection } = useSwipe({
    onSwipe: handleSubscriptionsSwipe,
    maxOffset: 100,
    ignoreSelector: '.pagination-dot',
  });

  const swipeRefStable = useRef(swipeRef);
  swipeRefStable.current = swipeRef;
  const setGridContainerRef = useCallback((el) => {
    gridContainerRef.current = el;
    if (swipeRefStable.current) swipeRefStable.current(el);
  }, []);

  // Fetch counts only on initial page load
  useEffect(() => {
    fetchSubscriptionCounts();
  }, [fetchSubscriptionCounts]);

  // Fetch subscriptions when filter changes (always reset to page 1)
  useEffect(() => {
    hasSubscriptionsRef.current = false;
    setCurrentPage(1);
    loadSubscriptions(1);
  }, [filter, loadSubscriptions]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      loadSubscriptions(page);
    }
  };

  if (loadingStates.initial) {
    return (
      <div className="modern-subscriptions-loading">
        <Loader color="#F334BE" height={30} width={30} />
        <h5>Loading subscriptions...</h5>
      </div>
    );
  }

  return (
    <div className="modern-subscriptions">
      <section className="filter-controls">
        <div className="stats-container">
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-indicator active"></div>
              <span className="stat-text">Active ({subscriptionCounts.active})</span>
            </div>
            <div className="stat-item">
              <div className="stat-indicator expiring"></div>
              <span className="stat-text">Expiring Soon ({subscriptionCounts.expiringSoon})</span>
            </div>
            <div className="stat-item">
              <div className="stat-indicator expired"></div>
              <span className="stat-text">Expired ({subscriptionCounts.expired})</span>
            </div>
          </div>
        </div>

        <div className="filter-header">
          <h2 className="filter-title">My Creators</h2>
        </div>

        <div className="filter-buttons">
          {FILTER_BUTTONS.map(({ value, label }) => (
            <button
              key={value}
              className={`filter-btn ${filter === value ? "active" : ""}`}
              onClick={() => (handleFilterChange(value), setFilterLabel(label))}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {loadingStates.subscriptions && !loadingStates.pagination && (
        <div className="modern-subscriptions-loading">
          <Loader color="#F334BE" height={30} width={30} />
          <h5>Loading {filterLabel}...</h5>
        </div>
      )}

      <section className="creator-grid p-0">
        {loadingStates.pagination && (
          <div className="pagination-loading-overlay">
            <div className="skeleton-spinner"></div>
          </div>
        )}

        <div
          ref={setGridContainerRef}
          className={`creator-grid-swipeable ${swipeOffset !== 0 ? 'swiping' : ''}`}
          {...swipeHandlers}
        >
          {errors.subscriptions ? (
            <div className="error-container">
              <div className="error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h4>Failed to load subscriptions</h4>
                <p>{errors.subscriptions}</p>
                <button
                  className="retry-btn-large"
                  onClick={() => loadSubscriptions(currentPage)}
                >
                  <i className="fa-solid fa-rotate"></i>
                  Try Again
                </button>
              </div>
            </div>
          ) : subscriptions.length !== 0 ? (
            <div
              className={`subscriptions-grid-wrapper ${isTransitioning ? 'transitioning' : ''} ${(transitionDirection || swipeDirection) ? `swipe-${transitionDirection || swipeDirection}` : ''}`}
              style={{
                transform: !isTransitioning && swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : '',
                opacity: swipeOffset !== 0 && !isTransitioning ? Math.max(0.75, 1 - Math.abs(swipeOffset) / 150) : 1,
              }}
            >
              <div className="cm_grid">
                {subscriptions.map((creator) => (
                  <CreatorCard
                    key={creator.subscriptionId}
                    creator={{
                      ...creator,
                      isSubscribed: creator.rol === ROLE.CREATOR_SUB ? true : false,
                      isFollowing: creator.rol === ROLE.CREATOR_CONTENT ? true : false,
                    }}
                    vatPercentage={vatPercentage}
                    isShowSubscriptionPrice={true}
                    isAllowFollowOrUnfollow={true}
                    onFollowSuccess={() => { }}
                    onUnfollowSuccess={() => {
                      setSubscriptions(prevSubscriptions =>
                        prevSubscriptions.filter(card => card.subscriptionId !== creator.subscriptionId)
                      );
                    }}
                    tab={filter}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="no-content">
              <p className="no-content-text">{EMPTY_MESSAGES[filter] || EMPTY_MESSAGES.all}</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="recommended-pagination">
              <div className="pagination-dots">
                {Array.from({ length: totalPages }, (_, index) => (
                  <span
                    key={index}
                    className={`pagination-dot cursor-pointer ${currentPage === index + 1 ? 'active' : ''}`}
                    onClick={() => handlePageChange(index + 1)}
                  ></span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* expiry list section */}
      <ExpiryList
        expiringSoonList={expiringSoonList}
        onSubscriptionSuccess={refreshAfterSubscriptionSuccess}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.user.user
});

export default connect(mapStateToProps)(SubscriptionsTab); 